// src/components/CustomerSummary/hooks/useCustomerSummaryController.js
// ============================================================================
// BLOCK CS.HOOK.0 — Customer Summary controller
// Purpose: single source of truth for all page state + actions
// ============================================================================

import React from 'react';

import {
  createCustomerSummarySave,
  deleteCustomerSummarySave,
  fetchCustomerSummarySave,
  fetchFacilities,
  generateCustomerSummary,
  generateCustomerSummarySection,
  // remove saveCustomerSummary
  listCustomerSummarySaves,
  updateCustomerSummarySave,
} from '../API/customerSummaryApi.js';
import { prettyDisplayFromIso } from '../components/SmartDateField.jsx';
import {
  buildDefaultExpanded,
  buildDefaultLocks,
  buildEmptySections,
  DEFAULT_SECTION_ORDER,
} from '../Constants/default.js';
import {
  buildWorkflowDistributionTextFromStudiesText,
  isDerivedSectionKey,
} from './derivedStudiesWorkflow.js';
import { readUiPrefsFromLocalStorage, writeUiPrefsToLocalStorage } from './uiPrefsStorage.js';
import { mergeGeneratedSections, moveItem } from './useSectionState.js';

// ============================================================================
// BLOCK CS.HOOK.1 — Date helpers (accept M/D/YYYY or YYYY-MM-DD; return YYYY-MM-DD)
// ============================================================================
function pad2(n) {
  return String(n).padStart(2, '0');
}

function isYmd(s) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(s || '').trim());
}

function toYmd(ui) {
  const raw = String(ui || '').trim();
  if (!raw) {
    return '';
  }

  if (isYmd(raw)) {
    return raw;
  }

  const m = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) {
    return '';
  }

  const mm = Number(m[1]);
  const dd = Number(m[2]);
  const yyyy = Number(m[3]);

  if (!yyyy || mm < 1 || mm > 12 || dd < 1 || dd > 31) {
    return '';
  }

  const d = new Date(yyyy, mm - 1, dd);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  if (d.getFullYear() !== yyyy || d.getMonth() !== mm - 1 || d.getDate() !== dd) {
    return '';
  }

  return `${yyyy}-${pad2(mm)}-${pad2(dd)}`;
}

export default function useCustomerSummaryController() {
  const [facilityName, setFacilityName] = React.useState('');

  const [startDateUi, setStartDateUi] = React.useState('');
  const [endDateUi, setEndDateUi] = React.useState('');

  const [startDateIso, setStartDateIso] = React.useState('');
  const [endDateIso, setEndDateIso] = React.useState('');

  const startDate = React.useMemo(
    () => startDateIso || toYmd(startDateUi),
    [startDateIso, startDateUi]
  );
  const endDate = React.useMemo(() => endDateIso || toYmd(endDateUi), [endDateIso, endDateUi]);

  const [facilities, setFacilities] = React.useState([]);
  const [loadingFacilities, setLoadingFacilities] = React.useState(false);

  const [order, setOrder] = React.useState(
    Array.isArray(DEFAULT_SECTION_ORDER) ? DEFAULT_SECTION_ORDER : []
  );
  const [sections, setSections] = React.useState(buildEmptySections);
  const [locks, setLocks] = React.useState(buildDefaultLocks);
  const [expanded, setExpanded] = React.useState(buildDefaultExpanded);

  const [generating, setGenerating] = React.useState(false);
  const [generatingKey, setGeneratingKey] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState(null);

  // ============================================================================
  // BLOCK CS.HOOK.UIREF.0 — uiPrefsRef (avoid no-use-before-define)
  // ----------------------------------------------------------------------------
  // Some callbacks are declared before uiPrefs state. ESLint disallows referencing
  // uiPrefs before it is defined, so we read it through a ref in those callbacks.
  // ============================================================================
  const uiPrefsRef = React.useRef({ tables: {} });

  // ============================================================================
  // BLOCK CS.HOOK.PSD.0 — Saved layouts (persistentsitedata)
  // ============================================================================
  const [saves, setSaves] = React.useState([]);
  const [selectedSave, setSelectedSave] = React.useState(''); // headerid string/number
  const [saveDialogOpen, setSaveDialogOpen] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [dialogName, setDialogName] = React.useState('');

  // load saved layouts list once
  React.useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const rows = await listCustomerSummarySaves();
        if (mounted) {
          setSaves(Array.isArray(rows) ? rows : []);
        }
      } catch {
        if (mounted) {
          setSaves([]);
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  // ============================================================================
  // BLOCK CS.HOOK.PSD.1 — Build config payload (NO uiPrefs here)
  // ----------------------------------------------------------------------------
  // NOTE
  // - uiPrefs is declared later in the file, so we attach uiPrefs at call sites
  //   (save/load) to avoid no-use-before-define.
  // ============================================================================
  const buildLayoutConfig = React.useCallback(() => {
    return {
      facilityName,
      startDate,
      endDate,
      order,
      locks,
      sections,
    };
  }, [facilityName, startDate, endDate, order, locks, sections]);

  // ============================================================================
  // BLOCK CS.HOOK.PSD.2 — Load a saved layout (robust response parsing)
  // ============================================================================
  const pendingUiPrefsRef = React.useRef(null);

  const loadSave = React.useCallback(async (headerid) => {
    const hid = headerid ? String(headerid) : '';
    setSelectedSave(hid);

    if (!hid) {
      return;
    }

    setError(null);

    try {
      const res = await fetchCustomerSummarySave(hid);

      // API returns { config: {...} }
      let cfg = res?.config;

      // Sometimes config is returned as a JSON string
      if (typeof cfg === 'string') {
        try {
          cfg = JSON.parse(cfg);
        } catch {
          cfg = null;
        }
      }

      if (!cfg || typeof cfg !== 'object') {
        throw new Error('Saved config is missing or invalid');
      }

      // Apply (guard each field)

      if (typeof cfg.facilityName === 'string') {
        setFacilityName(cfg.facilityName);
      }

      if (typeof cfg.startDate === 'string') {
        setStartDateIso(cfg.startDate);
        // Convert ISO format to display format for UI
        const displayStartDate = prettyDisplayFromIso(cfg.startDate);
        setStartDateUi(displayStartDate);
      }
      if (typeof cfg.endDate === 'string') {
        setEndDateIso(cfg.endDate);
        // Convert ISO format to display format for UI
        const displayEndDate = prettyDisplayFromIso(cfg.endDate);
        setEndDateUi(displayEndDate);
      }

      if (Array.isArray(cfg.order)) {
        setOrder(cfg.order);
      }
      if (cfg.locks && typeof cfg.locks === 'object') {
        setLocks(cfg.locks);
      }
      if (cfg.sections && typeof cfg.sections === 'object') {
        setSections(cfg.sections);
      }

      if (cfg.uiPrefs && typeof cfg.uiPrefs === 'object') {
        pendingUiPrefsRef.current = cfg.uiPrefs;
      } else {
        pendingUiPrefsRef.current = null;
      }
    } catch (e) {
      setError(e?.message || 'Failed to load saved layout');
    }
  }, []);

  // ============================================================================
  // BLOCK CS.HOOK.PSD.3 — Save / Update layout (read uiPrefs via ref)
  // ============================================================================
  const confirmSaveDialog = React.useCallback(async () => {
    const name = String(dialogName || '').trim();
    if (!name) {
      return;
    }

    setError(null);
    setSaving(true);

    try {
      const config = {
        ...buildLayoutConfig(),
        uiPrefs: uiPrefsRef.current,
      };

      const existing = saves.find((s) => s.savename === name);

      if (existing) {
        await updateCustomerSummarySave({ headerid: existing.headerid, config });
        setSelectedSave(String(existing.headerid));
      } else {
        const created = await createCustomerSummarySave({ savename: name, config });
        if (created?.headerid != null) {
          setSelectedSave(String(created.headerid));
        }
      }

      const rows = await listCustomerSummarySaves();
      setSaves(Array.isArray(rows) ? rows : []);
    } catch (e) {
      setError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
      setSaveDialogOpen(false);
      setDialogName('');
    }
  }, [dialogName, buildLayoutConfig, saves]);

  // ============================================================================
  // BLOCK CS.HOOK.PSD.4 — Delete selected layout
  // ============================================================================
  const confirmDelete = React.useCallback(async () => {
    if (!selectedSave) {
      return;
    }

    setError(null);
    setSaving(true);

    try {
      await deleteCustomerSummarySave(selectedSave);
      setSaves((prev) => prev.filter((s) => String(s.headerid) !== String(selectedSave)));
      setSelectedSave('');
    } catch (e) {
      setError(e?.message || 'Delete failed');
    } finally {
      setSaving(false);
      setDeleteDialogOpen(false);
    }
  }, [selectedSave]);

  // ============================================================================
  // BLOCK CS.HOOK.PSD.5 — HeaderPanel handlers (Save As / Update / Delete)
  // ============================================================================
  const saveAs = React.useCallback(
    async (savename) => {
      const name = String(savename || '').trim();
      if (!name) {
        return;
      }

      setDialogName(name);
      setError(null);
      setSaving(true);

      try {
        const config = { ...buildLayoutConfig(), uiPrefs: uiPrefsRef.current };

        const existing = saves.find((s) => s.savename === name);
        if (existing) {
          await updateCustomerSummarySave({ headerid: existing.headerid, config });
          setSelectedSave(String(existing.headerid));
        } else {
          const created = await createCustomerSummarySave({ savename: name, config });
          if (created?.headerid != null) {
            setSelectedSave(String(created.headerid));
          }
        }

        const rows = await listCustomerSummarySaves();
        setSaves(Array.isArray(rows) ? rows : []);
      } catch (e) {
        setError(e?.message || 'Save failed');
      } finally {
        setSaving(false);
        setDialogName('');
      }
    },
    [buildLayoutConfig, saves]
  );

  const updateSelectedSave = React.useCallback(async () => {
    if (!selectedSave) {
      return;
    }

    setError(null);
    setSaving(true);

    try {
      const config = { ...buildLayoutConfig(), uiPrefs: uiPrefsRef.current };
      await updateCustomerSummarySave({ headerid: selectedSave, config });

      const rows = await listCustomerSummarySaves();
      setSaves(Array.isArray(rows) ? rows : []);
    } catch (e) {
      setError(e?.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  }, [buildLayoutConfig, selectedSave]);

  const deleteSelectedSave = React.useCallback(async () => {
    await confirmDelete();
  }, [confirmDelete]);

  // =========================================================================
  // BLOCK CS.HOOK.UI.0 — UI prefs (shareable JSON + localStorage fallback)
  // -------------------------------------------------------------------------
  // GOAL
  // - uiPrefs travels with the saved JSON payload so other users see the same layout.
  // - localStorage is only a fallback so unsaved tweaks survive refresh.
  //
  // SHAPE
  // uiPrefs = {
  //   tables: {
  //     turnaroundTimes: { col1, col2, col3 },
  //     // other tables later...
  //   }
  // }
  // =========================================================================
  const [uiPrefs, setUiPrefs] = React.useState(() => {
    if (typeof window === 'undefined') {
      return { tables: {} };
    }
    return readUiPrefsFromLocalStorage() || { tables: {} };
  });

  // ============================================================================
  // BLOCK CS.HOOK.UIREF.1 — Keep uiPrefsRef in sync
  // ============================================================================
  React.useEffect(() => {
    uiPrefsRef.current = uiPrefs;
  }, [uiPrefs]);

  // ============================================================================
  // BLOCK CS.HOOK.PSD.2B — Apply deferred uiPrefs after loadSave
  // ============================================================================
  React.useEffect(() => {
    const next = pendingUiPrefsRef.current;
    if (!next) {
      return;
    }

    pendingUiPrefsRef.current = null;
    setUiPrefs(next);
  }, [selectedSave, order, setUiPrefs]);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    writeUiPrefsToLocalStorage(uiPrefs);
  }, [uiPrefs]);

  // Update one table’s widths under uiPrefs.tables[tableKey]
  const setTableColumnWidths = React.useCallback((tableKey, widths) => {
    const k = String(tableKey || '').trim();
    if (!k) {
      return;
    }

    setUiPrefs((prev) => ({
      ...(prev || {}),
      tables: {
        ...((prev && prev.tables) || {}),
        [k]: { ...(widths || {}) },
      },
    }));
  }, []);

  // =========================================================================
  // BLOCK CS.HOOK.2 — Load facilities (once on mount)
  // =========================================================================
  React.useEffect(() => {
    let mounted = true;

    async function load() {
      setLoadingFacilities(true);
      try {
        const res = await fetchFacilities();
        if (!mounted) {
          return;
        }

        const list = Array.isArray(res?.facilities) ? res.facilities : [];
        setFacilities(list);

        if (!facilityName && list.length > 0) {
          setFacilityName(list[0]);
        }
      } catch (e) {
        if (mounted) {
          setError(e?.message || 'Failed to load facilities');
        }
      } finally {
        if (mounted) {
          setLoadingFacilities(false);
        }
      }
    }

    load();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // =========================================================================
  // BLOCK CS.HOOK.2.1 — Derived sync (source -> workflow distribution)
  // =========================================================================
  React.useEffect(() => {
    const src = sections?.studiesStarted || '';
    const next = buildWorkflowDistributionTextFromStudiesText({
      studiesText: src,
      outputTitle: 'Studies Started Workflow Distribution',
      sectionMarkerContains: 'studies started',
    });

    setSections((prev) => {
      const current = prev?.studiesStartedWorkflowDistribution || '';
      if (current === next) {
        return prev;
      }
      return { ...prev, studiesStartedWorkflowDistribution: next };
    });
  }, [sections?.studiesStarted]);

  React.useEffect(() => {
    const src = sections?.finalizedStudies || '';
    const next = buildWorkflowDistributionTextFromStudiesText({
      studiesText: src,
      outputTitle: 'Finalized Studies Workflow Distributions',
      sectionMarkerContains: 'finalized studies',
    });

    setSections((prev) => {
      const current = prev?.finalizedStudiesWorkflowDistributions || '';
      if (current === next) {
        return prev;
      }
      return { ...prev, finalizedStudiesWorkflowDistributions: next };
    });
  }, [sections?.finalizedStudies]);

  const canGenerate = Boolean(facilityName && startDate && endDate);

  const toggleLock = React.useCallback((key) => {
    if (isDerivedSectionKey(key)) {
      return;
    }
    setLocks((p) => ({ ...p, [key]: !p[key] }));
  }, []);

  const toggleExpanded = React.useCallback((key) => {
    setExpanded((p) => ({ ...p, [key]: !p[key] }));
  }, []);

  const clearSection = React.useCallback((key) => {
    if (isDerivedSectionKey(key)) {
      return;
    }
    setSections((p) => ({ ...p, [key]: '' }));
  }, []);

  // ============================================================================
  // BLOCK CS.HOOK.3B — Regenerate a single section from backend
  // ============================================================================
  const autoPopulateSection = React.useCallback(
    async (key, opts = {}) => {
      const force = Boolean(opts?.force);

      if (isDerivedSectionKey(key)) {
        return;
      }
      if (!canGenerate) {
        return;
      }
      if (locks[key] && !force) {
        return;
      }

      setError(null);
      setGeneratingKey(key);

      try {
        const res = await generateCustomerSummarySection({
          facilityName,
          startDate,
          endDate,
          sectionKey: key,
        });

        if (!res?.ok) {
          throw new Error(res?.error || 'Generate section failed');
        }

        setSections((prev) => ({
          ...prev,
          [key]: String(res.text || ''),
        }));
      } catch (e) {
        setError(e?.message || 'Generate section failed');
      } finally {
        setGeneratingKey('');
      }
    },
    [canGenerate, facilityName, startDate, endDate, locks]
  );

  // ========================================================================
  // BLOCK CS.HOOK.2.2 — setSectionText (with cross-section refresh)
  // ------------------------------------------------------------------------
  // When Device Utilization is edited (Lost/Unreturned), we want Turnaround
  // Times to reflect that. We trigger a regenerate using the same logic as
  // autoPopulateSection, but inline to avoid no-use-before-define.
  // ========================================================================
  const setSectionText = React.useCallback(
    async (key, text) => {
      if (isDerivedSectionKey(key)) {
        return;
      }

      setSections((p) => ({ ...p, [key]: text }));

      if (key !== 'deviceUtilization') {
        return;
      }

      // If turnaroundTimes is locked or can't generate, do nothing.
      if (!canGenerate) {
        return;
      }
      if (locks.turnaroundTimes) {
        return;
      }

      setError(null);
      setGeneratingKey('turnaroundTimes');

      try {
        const res = await generateCustomerSummarySection({
          facilityName,
          startDate,
          endDate,
          sectionKey: 'turnaroundTimes',
        });

        if (!res?.ok) {
          throw new Error(res?.error || 'Generate section failed');
        }

        setSections((prev) => ({
          ...prev,
          turnaroundTimes: String(res.text || ''),
        }));
      } catch (e) {
        setError(e?.message || 'Generate section failed');
      } finally {
        setGeneratingKey('');
      }
    },
    [canGenerate, endDate, facilityName, locks, startDate]
  );

  const moveUp = React.useCallback((key) => {
    setOrder((p) => {
      const idx = p.indexOf(key);
      return idx <= 0 ? p : moveItem(p, idx, idx - 1);
    });
  }, []);

  const moveDown = React.useCallback((key) => {
    setOrder((p) => {
      const idx = p.indexOf(key);
      return idx < 0 || idx >= p.length - 1 ? p : moveItem(p, idx, idx + 1);
    });
  }, []);

  const generateAll = React.useCallback(async () => {
    if (!canGenerate) {
      return;
    }

    setError(null);
    setGenerating(true);

    // Clear all fields to defaults before generating new data
    setSections(buildEmptySections);
    setLocks(buildDefaultLocks);
    setExpanded(buildDefaultExpanded);
    setOrder(Array.isArray(DEFAULT_SECTION_ORDER) ? DEFAULT_SECTION_ORDER : []);

    try {
      const res = await generateCustomerSummary({ facilityName, startDate, endDate });
      if (!res?.ok) {
        throw new Error(res?.error || 'Generate failed');
      }

      setSections((prev) =>
        mergeGeneratedSections({
          prevSections: prev,
          nextSections: res.sections || {},
          locks,
        })
      );
    } catch (e) {
      setError(e?.message || 'Generate failed');
    } finally {
      setGenerating(false);
    }
  }, [canGenerate, facilityName, startDate, endDate, locks]);

  return {
    facilityName,
    setFacilityName,

    startDateUi,
    setStartDateUi,
    endDateUi,
    setEndDateUi,

    startDateIso,
    setStartDateIso,
    endDateIso,
    setEndDateIso,

    startDate,
    endDate,

    facilities,
    loadingFacilities,

    order,
    sections,
    locks,
    expanded,

    // ui prefs (shareable + localStorage fallback)
    uiPrefs,
    setUiPrefs,
    setTableColumnWidths,

    canGenerate,
    generating,
    generatingKey,
    saving,

    error,
    setError,

    toggleLock,
    toggleExpanded,
    clearSection,
    setSectionText,
    autoPopulateSection,
    moveUp,
    moveDown,

    generateAll,
    saves,
    selectedSave,
    setSelectedSave,
    saveDialogOpen,
    setSaveDialogOpen,
    deleteDialogOpen,
    setDeleteDialogOpen,
    dialogName,
    setDialogName,
    loadSave,
    confirmSaveDialog,
    confirmDelete,
    saveAs,
    updateSelectedSave,
    deleteSelectedSave,
  };
}
